import React, { useEffect, useState, useCallback } from "react";
import { Clock, Film, ImagePlus, Library, Play, Unlink } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { EmptyState, Modal, Spinner, btnGhost, btnPrimary, inputCls } from "./shared";
import GalleryMediaViewer from "../GalleryMediaViewer";

function LibraryModal({ linked, onAdd, onClose }) {
  const [pics, setPics] = useState(null);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState([]);
  useEffect(() => {
    api.get("/api/pictures/").then((r) => setPics(r.data || [])).catch(() => { toast.error("Could not load the photo library"); setPics([]); });
  }, []);
  const linkedIds = new Set(linked.map((p) => p.picture_id).filter(Boolean));
  const list = (pics || []).filter((p) => (p.media_type !== "video" || (p.mux_status === "ready" && p.poster_ready)) && (!q.trim() || [p.title, ...(p.tags || [])].some((t) => (t || "").toLowerCase().includes(q.toLowerCase()))));
  return (
    <Modal title="Link photos from library" onClose={onClose} wide
      footer={<><button type="button" className={btnGhost} onClick={onClose}>Cancel</button>
        <button type="button" className={btnPrimary} disabled={!picked.length} onClick={() => onAdd((pics || []).filter((p) => picked.includes(p.id)))}>Link {picked.length || ""}</button></>}>
      <input className={inputCls} placeholder="Search photos" value={q} onChange={(e) => setQ(e.target.value)} />
      {pics === null && <Spinner />}
      {pics && list.length === 0 && <EmptyState>No photos found.</EmptyState>}
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {list.map((p) => {
          const on = picked.includes(p.id);
          const already = linkedIds.has(p.id);
          const preview = p.poster_url || p.thumbnail_url || p.image_url;
          return (
            <button key={p.id} type="button" disabled={already} onClick={() => setPicked((s) => (on ? s.filter((x) => x !== p.id) : [...s, p.id]))}
              className={`relative rounded-lg overflow-hidden border-2 ${on ? "border-purple-400" : "border-transparent"} ${already ? "opacity-40" : ""}`} title={p.title}>
              <img src={preview} alt={p.title || ""} className="w-full aspect-square object-cover" loading="lazy" />
              {p.media_type === "video" && <span className="absolute inset-0 flex items-center justify-center"><Play size={24} className="fill-black/60 text-white" /></span>}
              {p.media_type === "video" && Number.isFinite(Number(p.duration)) && <span className="absolute bottom-1 right-1 rounded bg-black/80 px-1 text-[10px] text-white">{formatDuration(p.duration)}</span>}
              {(on || already) && <span className="absolute top-1 right-1 text-[10px] bg-purple-600 rounded px-1">{already ? "Linked" : "✓"}</span>}
            </button>
          );
        })}
      </div>
    </Modal>
  );
}

export default function LinkedPhotosTab({ record, setField }) {
  const photos = record.linked_photos || [];
  const [library, setLibrary] = useState(false);
  const [uploading, setUploading] = useState(0);
  const [galleryItems, setGalleryItems] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [previewPicture, setPreviewPicture] = useState(null);

  const loadGalleryItems = useCallback(async () => {
    if (!record.id) return;
    setGalleryLoading(true);
    try {
      const response = await api.get("/api/pictures/", { params: { linked_video_id: record.id } });
      setGalleryItems(response.data || []);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not load linked gallery media");
    } finally { setGalleryLoading(false); }
  }, [record.id]);

  useEffect(() => { loadGalleryItems(); }, [loadGalleryItems]);

  const linkedRecords = [
    ...galleryItems.map(picture => ({
      id: `gallery-${picture.id}`,
      picture_id: picture.id,
      title: picture.title || "Untitled",
      media_type: picture.media_type || "image",
      url: picture.image_url,
      image_url: picture.image_url,
      thumbnail_url: picture.thumbnail_url,
      poster_url: picture.poster_url,
      duration: picture.duration,
      picture,
    })),
    ...photos.filter(photo => !photo.picture_id || !galleryItems.some(picture => picture.id === photo.picture_id))
      .map(photo => ({ ...photo, media_type: photo.media_type || "image" })),
  ];

  const addGalleryItems = async (selected) => {
    const attached = [];
    const legacyImages = [];
    for (const picture of selected) {
      try {
        await api.put(`/api/pictures/${picture.id}`, { linked_video_id: record.id });
        attached.push({
          id: `p${picture.id}`,
          picture_id: picture.id,
          url: picture.image_url || picture.poster_url || picture.thumbnail_url,
          title: picture.title || "",
          media_type: picture.media_type || "image",
        });
        if ((picture.media_type || "image") === "image") {
          legacyImages.push({
            id: `p${picture.id}`,
            picture_id: picture.id,
            url: picture.image_url,
            title: picture.title || "",
          });
        }
      } catch (error) {
        toast.error(error?.response?.data?.detail || `Could not link ${picture.title || "gallery item"}`);
      }
    }
    if (attached.length) {
      if (legacyImages.length) setField("linked_photos", [...photos, ...legacyImages]);
      await loadGalleryItems();
    }
    setLibrary(false);
  };

  const unlink = async (photo) => {
    if (photo.picture_id) {
      try {
        await api.put(`/api/pictures/${photo.picture_id}`, { linked_video_id: null });
        await loadGalleryItems();
      } catch (error) {
        toast.error(error?.response?.data?.detail || "Could not unlink gallery item");
        return;
      }
    }
    const remainingLegacy = photos.filter(item => item.id !== photo.id && (!photo.picture_id || item.picture_id !== photo.picture_id));
    if (remainingLegacy.length !== photos.length) {
      setField("linked_photos", remainingLegacy);
    }
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(files.length);
    const added = [];
    for (const f of files) {
      if (f.size > 50 * 1024 * 1024) { toast.error(`${f.name} is over 50MB and was skipped`); continue; }
      try {
        const form = new FormData();
        form.append("file", f);
        form.append("folder", "videos/linked-photos");
        form.append("resource_type", "auto");
        const up = await api.post("/api/cloudinary/upload", form);
        const url = up.data.secure_url;
        let pictureId = null;
        try {
          const pic = await api.post("/api/pictures/", { title: f.name.replace(/\.[^.]+$/, ""), image_url: url, thumbnail_url: url, linked_video_id: record.id });
          pictureId = pic.data?.id || null;
        } catch { /* photo stays linked even if the library record could not be created */ }
        added.push({ id: `p${Date.now().toString(36)}${added.length}`, picture_id: pictureId, url, title: f.name.replace(/\.[^.]+$/, "") });
      } catch (err) {
        toast.error(`${f.name}: ${err?.response?.data?.detail || "upload failed"}`);
      }
    }
    setUploading(0);
    if (added.length) setField("linked_photos", [...photos, ...added]);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 justify-between items-center">
        <p className="text-xs text-zinc-500">Gallery items are linked by Picture ID; unlinking preserves the media record and stored asset.</p>
        <div className="flex gap-2">
          <button type="button" className={btnGhost} onClick={() => setLibrary(true)}><Library size={14} className="inline mr-1" />From library</button>
          <label className={`${btnPrimary} cursor-pointer`}><ImagePlus size={14} className="inline mr-1" />{uploading ? `Uploading ${uploading}...` : "Upload"}
            <input type="file" accept="image/*" multiple className="hidden" onChange={onFiles} disabled={uploading > 0} /></label>
        </div>
      </div>
      {galleryLoading && <Spinner />}
      {linkedRecords.length === 0 && !uploading && !galleryLoading && <EmptyState>No gallery media linked yet.</EmptyState>}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {linkedRecords.map((p) => (
          <div key={p.id} className="rounded-xl border border-zinc-800 bg-black/40 overflow-hidden" data-testid={`photo-${p.id}`}>
            <button type="button" className="relative block w-full" onClick={() => setPreviewPicture(p.picture || { id: p.picture_id || p.id, title: p.title, media_type: p.media_type, image_url: p.url, thumbnail_url: p.thumbnail_url, poster_url: p.poster_url, duration: p.duration })}>
              <img src={p.poster_url || p.thumbnail_url || p.url} alt={p.title} className="w-full aspect-square object-cover" loading="lazy" />
              {p.media_type === "video" && <span className="absolute inset-0 flex items-center justify-center"><Play size={28} className="fill-black/60 text-white" /></span>}
              {p.media_type === "video" && Number.isFinite(Number(p.duration)) && <span className="absolute bottom-2 right-2 rounded bg-black/80 px-1.5 py-0.5 text-[10px] text-white">{formatDuration(p.duration)}</span>}
            </button>
            <div className="flex items-center gap-1 p-2">
              <span className="text-xs text-zinc-300 truncate flex-1" title={p.title}>{p.title || "Untitled"}</span>
              <button type="button" aria-label={`Unlink ${p.title || "photo"}`} onClick={() => unlink(p)} className="p-1 rounded hover:bg-red-900/40 text-zinc-500 hover:text-red-400"><Unlink size={14} /></button>
            </div>
          </div>
        ))}
      </div>
      {library && <LibraryModal linked={linkedRecords} onClose={() => setLibrary(false)}
        onAdd={addGalleryItems} />}
      {previewPicture && <GalleryMediaViewer picture={previewPicture}
        playbackUrl={previewPicture.media_type === "video" ? `/api/pictures/clips/${previewPicture.id}/playback-token` : null}
        onClose={() => setPreviewPicture(null)} />}
    </div>
  );
}

function formatDuration(value) {
  const seconds = Math.max(0, Math.floor(Number(value) || 0));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}
